import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_ITEMS, PORTAL_URL } from "@/lib/data";
import { useRegisterModal } from "@/context/RegisterModalContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openModal } = useRegisterModal();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Defensive: clear any stale scroll-lock left over from a previous render
    // (e.g. a hot-reload during development) so a fresh page load never starts stuck.
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-black/40 backdrop-blur-xl border-b border-white/[0.04]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[76px] flex items-center justify-between">
          <a href="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
            <img src="/logo-dark.png" alt="Doble R Records" className="h-8 w-8 object-contain" />
            <span className="font-heading font-bold text-white text-[15px] leading-tight tracking-tight hidden sm:block">
              DOBLE R RECORDS<br />
              <span className="text-[10px] text-neutral-400 font-medium tracking-[0.2em]">DISTRIBUTION</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={`/${item.href}`}
                data-testid={`navbar-link-${item.key}`}
                className="text-[13px] font-medium text-neutral-400 hover:text-white transition-colors duration-200 tracking-wide"
              >
                {t.nav[item.key]}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-login-button"
              className="text-[13px] font-semibold text-white/90 hover:text-brand-blue px-4 py-2 transition-colors duration-200"
            >
              {t.nav.login}
            </a>
            <button
              data-testid="navbar-register-button"
              onClick={openModal}
              className="group relative overflow-hidden bg-brand-red text-white text-[13px] font-bold px-5 py-2.5 rounded-full flex items-center gap-1.5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_6px_20px_-6px_rgba(255,36,54,0.5)]"
            >
              {t.nav.register}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <button
              data-testid="navbar-mobile-quick-register-button"
              onClick={openModal}
              className="bg-brand-red text-white text-[12px] font-bold px-3.5 py-2 rounded-full whitespace-nowrap"
            >
              {t.nav.register}
            </button>
            <button
              data-testid="navbar-mobile-toggle"
              className="text-white p-2.5 -mr-2.5"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Cerrar menú" : "Abrir menú"}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden fixed inset-0 top-[76px] z-40 bg-black/95 backdrop-blur-2xl overflow-y-auto px-6 py-8 flex flex-col gap-2"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={`/${item.href}`}
              onClick={() => setOpen(false)}
              data-testid={`navbar-mobile-link-${item.key}`}
              className="font-heading font-bold text-2xl text-white py-3.5 border-b border-white/[0.06] active:text-brand-red transition-colors duration-150"
            >
              {t.nav[item.key]}
            </a>
          ))}
          <div className="flex flex-col gap-3 pt-6 mt-2">
            <LanguageSwitcher variant="mobile" />
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-mobile-login-button"
              className="text-sm font-semibold text-white text-center py-3.5 border border-white/20 rounded-full"
            >
              {t.nav.login}
            </a>
            <button
              data-testid="navbar-mobile-register-button"
              onClick={() => { setOpen(false); openModal(); }}
              className="text-sm font-bold text-white bg-brand-red text-center py-3.5 rounded-full"
            >
              {t.nav.register}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
